import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  baseName,
  formatBytes,
  formatUploadedAt,
} from 'scaleflow/utils/manual-repo-archives';
import RefreshCw from 'scaleflow/components/tailwind-icons/refresh-cw';
import PlusIcon from 'scaleflow/components/tailwind-icons/plus';

export interface ManualUploadsArchivePickerSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      archives: S3ObjectSummary[];
      /** Repo names (base names) already on this scan, so they cannot be re-added. */
      existingNames?: string[];
      isLoading?: boolean;
      disabled?: boolean;
      isAdding?: boolean;
      onAdd: (filenames: string[]) => Promise<void> | void;
    };
  };
}

type Row = {
  name: string;
  repoName: string;
  sizeText: string;
  uploadedText: string;
  alreadyAdded: boolean;
  isChecked: boolean;
};

/**
 * Picks archives out of the tenant's library to add to this scan.
 *
 * Kept separate from `ManualUploads::ArchiveList` rather than adding
 * selectable/deletable modes to one component: deleting has exactly one home
 * (the connector page), and a single component juggling both roles would carry
 * two sets of state for no shared behaviour beyond a table.
 */
export default class ManualUploadsArchivePicker extends Component<ManualUploadsArchivePickerSignature> {
  @tracked selected = new Set<string>();

  private get existingNames(): Set<string> {
    return new Set(this.args.existingNames ?? []);
  }

  get rows(): Row[] {
    return [...(this.args.archives ?? [])]
      .sort((a, b) => {
        const at = a.lastModified?.getTime() ?? 0;
        const bt = b.lastModified?.getTime() ?? 0;
        return bt - at;
      })
      .map((archive) => {
        const repoName = baseName(archive.name);
        const alreadyAdded = this.existingNames.has(repoName);
        return {
          name: archive.name,
          repoName,
          sizeText: formatBytes(archive.size),
          uploadedText: formatUploadedAt(archive.lastModified),
          alreadyAdded,
          // An archive already on the scan reads as checked because it *is*
          // included — it just cannot be added twice.
          isChecked: alreadyAdded || this.selected.has(archive.name),
        };
      });
  }

  get selectedCount(): number {
    return this.rows.filter((row) => !row.alreadyAdded && row.isChecked).length;
  }

  private get selectableNames(): string[] {
    return this.rows.filter((row) => !row.alreadyAdded).map((row) => row.name);
  }

  get allSelected(): boolean {
    const names = this.selectableNames;
    return names.length > 0 && names.every((name) => this.selected.has(name));
  }

  get someSelected(): boolean {
    return this.selectedCount > 0 && !this.allSelected;
  }

  get selectAllDisabled(): boolean {
    return !this.selectableNames.length || Boolean(this.args.disabled);
  }

  get addDisabled(): boolean {
    return (
      !this.selectedCount ||
      Boolean(this.args.disabled) ||
      Boolean(this.args.isAdding)
    );
  }

  @action
  toggle(filename: string) {
    // Reassigned rather than mutated: a Set mutated in place is not tracked.
    const next = new Set(this.selected);
    if (next.has(filename)) next.delete(filename);
    else next.add(filename);
    this.selected = next;
  }

  @action
  toggleAll() {
    this.selected = this.allSelected
      ? new Set()
      : new Set(this.selectableNames);
  }

  @action
  async add() {
    if (this.addDisabled) return;
    const filenames = this.rows
      .filter((row) => !row.alreadyAdded && row.isChecked)
      .map((row) => row.name);

    try {
      await this.args.onAdd(filenames);
      // Cleared only once the caller resolves.
      this.selected = new Set();
    } catch (error) {
      // Swallowed rather than rethrown: this runs from a click handler, where a
      // rejection surfaces as an unhandled promise rejection instead of
      // anything the user can see. The selection is left ticked so a retry
      // needs no re-picking.
      console.error('Failed to add repositories from the library', error);
    }
  }

  <template>
    <div data-test-archive-picker ...attributes>
      {{#if @isLoading}}
        <div class="py-6 text-center" data-test-picker-loading>
          <RefreshCw class="w-5 h-5 animate-spin text-gray-400 mx-auto" />
          <p class="text-xs text-gray-500 mt-2">
            {{t "manual-uploads.pick-loading"}}
          </p>
        </div>
      {{else if this.rows.length}}
        <div
          class="rounded-md border divide-y divide-gray-200 max-h-96 overflow-y-auto"
        >
          <label
            class="sticky top-0 z-10 flex items-center gap-3 px-4 py-2.5 text-sm bg-gray-50
              {{if
                this.selectAllDisabled
                'opacity-60 cursor-not-allowed'
                'cursor-pointer'
              }}"
          >
            <input
              type="checkbox"
              class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              data-test-picker-select-all
              checked={{this.allSelected}}
              indeterminate={{this.someSelected}}
              disabled={{this.selectAllDisabled}}
              {{on "change" this.toggleAll}}
            />
            <span class="font-medium text-gray-700">
              {{t "manual-uploads.pick-select-all"}}
            </span>
          </label>
          {{#each this.rows key="name" as |row|}}
            <label
              class="flex items-center gap-3 px-4 py-2.5 text-sm
                {{if
                  row.alreadyAdded
                  'opacity-60 cursor-not-allowed'
                  'cursor-pointer hover:bg-gray-50'
                }}"
              data-test-picker-row={{row.name}}
            >
              <input
                type="checkbox"
                class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                data-test-picker-checkbox={{row.name}}
                checked={{row.isChecked}}
                disabled={{if row.alreadyAdded true @disabled}}
                {{on "change" (fn this.toggle row.name)}}
              />
              <span class="flex-1 min-w-0">
                <span class="font-medium text-gray-900 break-all">
                  {{row.repoName}}
                </span>
                {{#if row.alreadyAdded}}
                  <span
                    class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                    data-test-already-added
                  >
                    {{t "manual-uploads.pick-already-added"}}
                  </span>
                {{/if}}
              </span>
              <span class="text-xs text-gray-500 whitespace-nowrap">
                {{row.sizeText}}
              </span>
              <span
                class="text-xs text-gray-400 whitespace-nowrap hidden sm:inline"
              >
                {{row.uploadedText}}
              </span>
            </label>
          {{/each}}
        </div>

        <div class="flex justify-end mt-3">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            data-test-add-repositories
            disabled={{this.addDisabled}}
            {{on "click" this.add}}
          >
            {{#if @isAdding}}
              <RefreshCw class="w-3.5 h-3.5 animate-spin" />
            {{else}}
              <PlusIcon class="w-3.5 h-3.5" />
            {{/if}}
            {{t "manual-uploads.add-repositories" count=this.selectedCount}}
          </button>
        </div>
      {{else}}
        <div class="py-6 text-center" data-test-picker-empty>
          <p class="text-sm font-medium text-gray-900">
            {{t "manual-uploads.pick-empty"}}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            {{t "manual-uploads.pick-empty-hint"}}
          </p>
        </div>
      {{/if}}
    </div>
  </template>
}
